/**
 * Email Service for Proxy Emobility
 * Handles email sending through Google Apps Script Webhook
 */

// Email service configuration
const EMAIL_CONFIG = {
  recipient: "hello@proxyemobility.com",
  company: "Proxy Emobility"
};

/**
 * Gets the Google Apps Script webhook URL
 */
function getWebhookUrl(): string {
  const webhookUrl = import.meta.env.VITE_GOOGLE_WEBHOOK_URL;

  if (!webhookUrl) {
    console.error('VITE_GOOGLE_WEBHOOK_URL non configurée dans .env');
    throw new Error('Configuration webhook manquante. Veuillez configurer VITE_GOOGLE_WEBHOOK_URL dans .env');
  }

  return webhookUrl;
}

/**
 * Sends an email using Google Apps Script Webhook
 * @param subject Email subject
 * @param body Email body (HTML supported)
 * @param fromName Sender name
 * @param fromEmail Sender email
 * @returns Promise resolving to success status
 */
export const sendEmail = async (
  subject: string,
  body: string,
  fromName: string = "Visiteur du site",
  fromEmail: string = "no-reply@proxyemobility.com"
): Promise<boolean> => {
  try {
    const webhookUrl = getWebhookUrl();

    console.log('Envoi email via Google Apps Script Webhook');

    const response = await fetch(webhookUrl, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        formType: 'email',
        formData: {
          subject,
          message: body,
          from_name: fromName,
          from_email: fromEmail
        },
        customerEmail: fromEmail,
        customerName: fromName,
        ipAddress: typeof window !== 'undefined' ? window.location.hostname : undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      }),
    });

    console.log('Email envoyé avec succès via Google Apps Script');
    return true;

  } catch (error) {
    console.error("Échec d'envoi d'email via Google Apps Script", error);
    throw error;
  }
};

/**
 * Formats form data into an HTML table
 * @param formData Form data to format
 * @param formName Name of the form for the header
 * @returns HTML string with formatted table
 */
export const formatFormDataToHtml = (formData: FormData, formName: string): string => {
  let html = `<h2>${formName}</h2>\n\n<table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; width: 100%;">`;
  
  // Add table header
  html += `<tr style="background-color: #f2f2f2;">
    <th style="text-align: left; padding: 8px;">Champ</th>
    <th style="text-align: left; padding: 8px;">Valeur</th>
  </tr>`;
  
  // Add all form fields to the table
  formData.forEach((value, key) => {
    // Skip internal fields
    if (key.startsWith('_')) return;
    
    // Format field name for better readability
    const fieldName = key
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    html += `<tr>
      <td style="padding: 8px; border: 1px solid #ddd;"><strong>${fieldName}</strong></td>
      <td style="padding: 8px; border: 1px solid #ddd;">${value}</td>
    </tr>`;
  });
  
  html += '</table>';
  return html;
};

/**
 * Sends form submission notification to Proxy Emobility via Google Apps Script Webhook
 * @param formType Type of form (e.g., "Réservation Teranga+", "Contact", "Précommande")
 * @param formData Object containing all form data
 * @returns Promise resolving to success status
 */
export const sendFormNotification = async (
  formType: string,
  formData: Record<string, any>
): Promise<boolean> => {
  try {
    const webhookUrl = getWebhookUrl();

    // Extract customer info
    const customerEmail = formData.email || formData.customer_email || formData.contact_email;
    const customerName = formData.nom && formData.prenom
      ? `${formData.prenom} ${formData.nom}`
      : formData.name || formData.full_name || formData.fullName || formData.entreprise || formData.company_name;

    // Convert formType to lowercase key
    const formTypeKey = getFormTypeKey(formType);

    console.log('Envoi notification formulaire:', formType, 'via Google Apps Script');

    const response = await fetch(webhookUrl, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        formType: formTypeKey,
        formData,
        customerEmail,
        customerName,
        ipAddress: typeof window !== 'undefined' ? window.location.hostname : undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      }),
    });

    console.log('Notification envoyée avec succès via Google Apps Script');
    return true;

  } catch (error) {
    console.error('Échec envoi notification:', error);
    return false;
  }
};

/**
 * Converts form type display name to lowercase key for edge function
 */
function getFormTypeKey(formType: string): string {
  const lowerType = formType.toLowerCase();

  if (lowerType.includes('contact')) return 'contact';
  if (lowerType.includes('réservation') || lowerType.includes('reservation')) return 'reservation';
  if (lowerType.includes('précommande') || lowerType.includes('preorder')) return 'preorder';
  if (lowerType.includes('pay molo') || lowerType.includes('paymolo')) return 'paymolo';
  if (lowerType.includes('simulation')) return 'simulation';
  if (lowerType.includes('essai') || lowerType.includes('trial')) return 'trial';
  if (lowerType.includes('formation') || lowerType.includes('training')) return 'training';
  if (lowerType.includes('teranga')) return 'reservation';
  if (lowerType.includes('investisseur') || lowerType.includes('investor')) return 'investor';

  return 'contact';
}

export default {
  sendEmail,
  formatFormDataToHtml,
  sendFormNotification
};